import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { productsApi, stockApi } from '../api/client';
import QRScanner from '../components/QRScanner';
import Card from '../components/Card';
import Button from '../components/Button';
import Input from '../components/Input';
import useStore from '../store/useStore';
import { parseQrCode } from '../utils/qrParser';

// Функция форматирования количества: целые числа без десятичной части, дробные с одним знаком
const formatQty = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return '0';
  return num % 1 === 0 ? num.toString() : num.toFixed(1);
};

const Scan = () => {
  const navigate = useNavigate();
  const { setScannedQr } = useStore();
  const [scannedProduct, setScannedProduct] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [searchMode, setSearchMode] = useState(true); // Режим поиска вместо сканирования
  const [itemCode, setItemCode] = useState('');
  const [error, setError] = useState(null);
  const isProcessingRef = useRef(false); // Флаг для предотвращения повторных запросов
  const lastProcessedQrRef = useRef(null); // Последний обработанный QR-код

  const handleQrScan = async (qr) => {
    // Предотвращаем повторную обработку того же QR-кода
    if (isProcessingRef.current) {
      console.log('⏸️ QR scan request already processing, ignoring duplicate');
      return;
    }
    
    // Игнорируем повторные запросы для того же QR-кода
    if (lastProcessedQrRef.current === qr) {
      console.log('⏸️ Same QR code already processed, ignoring');
      return;
    }
    
    console.log('🔄 Processing QR scan:', qr);
    
    // Устанавливаем флаги
    isProcessingRef.current = true;
    lastProcessedQrRef.current = qr;
    
    try {
      // Парсим QR-код для извлечения product_code
      const qrData = parseQrCode(qr);
      const productCode = qrData?.product_code;
      
      if (!productCode) {
        throw new Error('Не удалось извлечь код товара из QR-кода');
      }
      
      // Ищем товар по product_code
      const response = await productsApi.getByPc(productCode);
      const product = response.data;
      
      // Получаем остатки товара
      try {
        const stockResponse = await stockApi.getStock(product.id);
        product.stock = stockResponse.data?.total_stock ?? stockResponse.data?.stock ?? 0;
      } catch (stockError) {
        console.warn('Не удалось получить остатки товара:', stockError);
        product.stock = 0;
      }
      
      setScannedProduct(product);
      setScannedQr(qr);
      setShowScanner(false);
      setError(null);
    } catch (err) {
      if (err.response?.status === 404) {
        // Товар не найден - сохраняем QR для создания нового товара
        setError('Товар не найден');
        setScannedProduct(null);
        setScannedQr(qr); // Важно: сохраняем QR-код для автозаполнения
        setShowScanner(false);
      } else {
        setError('Ошибка сканирования QR-кода');
        // Сохраняем QR даже при ошибке, чтобы можно было создать товар вручную
        setScannedQr(qr);
      }
    } finally {
      // Сбрасываем флаг после завершения обработки
      // Используем небольшую задержку, чтобы предотвратить мгновенные повторные сканирования
      setTimeout(() => {
        isProcessingRef.current = false;
      }, 1000);
    }
  };

  const handleCreateProduct = () => {
    navigate('/products/new');
  };

  const handleViewProduct = () => {
    if (scannedProduct) {
      navigate(`/products/${scannedProduct.id}`);
    }
  };

  const handleSearchByItemCode = async () => {
    if (!itemCode.trim()) {
      setError('Введите артикул товара');
      return;
    }

    if (isProcessingRef.current) {
      return;
    }

    isProcessingRef.current = true;
    setError(null);

    try {
      const response = await productsApi.getByItemCode(itemCode.toUpperCase());
      const product = response.data;

      // Получаем остатки товара
      try {
        const stockResponse = await stockApi.getStock(product.id);
        product.stock = stockResponse.data?.total_stock ?? stockResponse.data?.stock ?? 0;
      } catch (stockError) {
        console.warn('Не удалось получить остатки товара:', stockError);
        product.stock = 0;
      }

      setScannedProduct(product);
      setSearchMode(false);
      setError(null);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Товар не найден');
        setScannedProduct(null);
      } else {
        setError('Ошибка поиска товара');
      }
    } finally {
      isProcessingRef.current = false;
    }
  };

  const handleStartScan = () => {
    setShowScanner(true);
    setSearchMode(false);
    setError(null);
    setScannedProduct(null);
    setItemCode('');
  };

  if (showScanner) {
    return (
      <QRScanner
        onScan={handleQrScan}
        onError={(err) => {
          setError(`Ошибка сканирования: ${err}`);
          setShowScanner(false);
          setSearchMode(true);
        }}
        onClose={() => {
          setShowScanner(false);
          setSearchMode(true);
        }}
      />
    );
  }

  if (searchMode) {
    return (
      <div className="p-4">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Поиск товара</h1>
        
        <Card className="mb-4">
          <Input
            label="Артикул"
            value={itemCode}
            onChange={(e) => setItemCode(e.target.value.toUpperCase())}
            placeholder="Введите артикул товара"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearchByItemCode();
              }
            }}
          />
        </Card>

        {error && (
          <Card className="mb-4 bg-red-50">
            <p className="text-red-600 font-semibold mb-2">{error}</p>
          </Card>
        )}

        <div className="space-y-3">
          <Button
            variant="primary"
            size="lg"
            className="w-full"
            onClick={handleSearchByItemCode}
            disabled={isProcessingRef.current || !itemCode.trim()}
          >
            🔍 Найти
          </Button>
          <Button
            variant="secondary"
            size="lg"
            className="w-full"
            onClick={handleStartScan}
          >
            📷 Сканировать QR-код
          </Button>
          <Button
            variant="secondary"
            size="md"
            className="w-full"
            onClick={() => navigate('/')}
          >
            На главную
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      {scannedProduct ? (
        <>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Товар найден</h1>
          <Card className="mb-4">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  {scannedProduct.name}
                </h3>
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  {(scannedProduct.diameter !== null && scannedProduct.diameter !== undefined) && (
                    <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
                      Диаметр: {parseFloat(scannedProduct.diameter).toFixed(1)}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-4 mb-2">
                  <p className={`text-sm font-semibold ${
                    (scannedProduct.stock || 0) > 0 
                      ? 'text-green-600' 
                      : 'text-red-600'
                  }`}>
                    Остаток: {formatQty(scannedProduct.stock || 0)}
                  </p>
                </div>
                <p className="text-lg font-bold text-blue-600">
                  {parseFloat(scannedProduct.price).toFixed(2)} ₸
                </p>
              </div>
            </div>
          </Card>
          <div className="space-y-3">
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              onClick={handleViewProduct}
            >
              Просмотр товара
            </Button>
            <Button
              variant="secondary"
              size="lg"
              className="w-full"
              onClick={() => {
                // Сбрасываем флаги для нового поиска
                isProcessingRef.current = false;
                lastProcessedQrRef.current = null;
                setScannedProduct(null);
                setSearchMode(true);
                setItemCode('');
                setError(null);
              }}
            >
              Новый поиск
            </Button>
          </div>
        </>
      ) : (
        <>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Результат сканирования</h1>
          {error && (
            <Card className="mb-4 bg-red-50">
              <p className="text-red-600 font-semibold mb-2">{error}</p>
            </Card>
          )}
          <div className="space-y-3">
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              onClick={handleCreateProduct}
            >
              Создать новый товар
            </Button>
            <Button
              variant="secondary"
              size="lg"
              className="w-full"
              onClick={() => {
                // Сбрасываем флаги для нового поиска
                isProcessingRef.current = false;
                lastProcessedQrRef.current = null;
                setScannedProduct(null);
                setSearchMode(true);
                setItemCode('');
                setError(null);
              }}
            >
              Новый поиск
            </Button>
            <Button
              variant="secondary"
              size="md"
              className="w-full"
              onClick={() => navigate('/')}
            >
              На главную
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default Scan;
